import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, MapPin, Palette } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { themeOf, tint } from "@/lib/mall";
import { BillboardTag } from "@/components/mall/StoreTag";
import OpsChips from "@/components/mall/OpsChips";

/**
 * A shop's sign, painted in its colors. `mono` is the directory's version: plain ink, no shop
 * chrome — the platform talking, not a tenant.
 */
export default function Billboard({ board, preview = false, mono = false }) {
    const { enter } = useMall();
    const navigate = useNavigate();
    const card = board.store || {};
    const theme = mono ? { primary: "#ffffff", secondary: "#0B0B0B", tertiary: "#A1A1AA", accent: "#ffffff", on: "#0B0B0B", onSecondary: "#ffffff" } : themeOf(card);
    const product = board.product;
    const art = board.image || product?.image || card.banner;
    const url = board.cta_url || (mono ? "/shirts" : `/stores/${card.slug}`);

    const walkIn = () => {
        if (preview) return;
        if (!mono) enter(card, "billboard");
        navigate(url);
    };

    return (
        <motion.section initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className={`dark-block relative overflow-hidden border-y ${preview ? "pointer-events-none rounded-3xl" : ""}`}
            style={{ borderColor: tint(theme.primary, 0.3), background: theme.secondary }}
            data-testid={mono ? `directory-billboard-${board.id}` : `billboard-${card.slug || "preview"}`}>
            {art && <img src={mediaUrl(art)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
            <div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${theme.secondary} 10%, ${tint(theme.secondary, 0.85)} 52%, ${tint(theme.primary, 0.25)} 100%)` }} />
            <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full blur-[130px]" style={{ background: tint(theme.primary, 0.35) }} />

            <div className="relative mx-auto grid max-w-7xl items-center gap-8 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <div className="mb-5 flex flex-wrap items-center gap-2">
                        {mono ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300" data-testid="billboard-directory-tag">
                                13thav directory
                            </span>
                        ) : <BillboardTag card={card} />}
                        {!mono && <Link to={`/stores/${card.slug}`} data-testid={`billboard-store-${card.slug}`}
                            className="inline-flex flex-wrap items-center gap-2 rounded-full border bg-black/40 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em]"
                            style={{ borderColor: tint(theme.tertiary, 0.6), color: theme.tertiary }}>
                            {card.verified && <BadgeCheck size={12} />} {card.name} · {card.tier_label}
                            <span className="inline-flex items-center gap-1 text-zinc-400"><MapPin size={10} /> {card.city}</span>
                        </Link>}
                    </div>

                    <h2 className="font-display text-4xl font-black leading-[0.94] tracking-tighter sm:text-5xl lg:text-6xl"
                        data-testid={`billboard-headline-${card.slug || "preview"}`}>
                        {board.headline || "Your headline goes here."}
                    </h2>
                    {board.subhead && <p className="mt-5 max-w-xl text-base text-zinc-300 md:text-lg">{board.subhead}</p>}

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <button onClick={walkIn} data-testid={`billboard-cta-${card.slug || "preview"}`}
                            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wide transition-transform duration-200 hover:scale-[1.03]"
                            style={{ background: theme.primary, color: theme.on }}>
                            {board.cta_label || (mono ? "Start shopping" : `Shop ${card.name || "the shop"}`)} <ArrowRight size={15} />
                        </button>
                        {!mono && <Link to={`/stores/${card.slug}`} className="rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-zinc-200 transition-colors hover:border-white/50 hover:text-white">
                            Everything in this shop →
                        </Link>}
                    </div>

                    {!mono && (
                        <>
                            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                                {card.designers && <span className="inline-flex items-center gap-1.5" style={{ color: theme.accent }}><Palette size={11} /> live designers</span>}
                                {card.product_count > 0 && <span>{card.product_count} products</span>}
                                {card.listing_count > 0 && <span>{card.listing_count} ads</span>}
                            </div>
                            <OpsChips card={card} className="mt-4" dark />
                        </>
                    )}
                </div>

                <div className="relative lg:col-span-5">
                    {art && (
                        <div className="relative">
                            <div className="absolute -inset-3 rounded-[2rem] blur-3xl" style={{ background: tint(theme.primary, 0.25) }} />
                            <div className="relative overflow-hidden rounded-[2rem] border" style={{ borderColor: tint(theme.primary, 0.35) }}>
                                <img src={mediaUrl(art)} alt={board.headline || card.name}
                                    className="h-56 w-full object-cover sm:h-72 lg:h-80" data-testid={`billboard-art-${card.slug || "preview"}`} />
                            </div>
                        </div>
                    )}
                    {product?.name && !mono && (
                        <Link to={product.url} data-testid={`billboard-product-${card.slug}`}
                            className="group mt-4 flex items-center gap-3 rounded-2xl border p-3 pr-5 transition-colors"
                            style={{ borderColor: tint(theme.primary, 0.35), background: tint(theme.primary, 0.07) }}>
                            {product.image && <img src={mediaUrl(product.image)} alt="" className="h-12 w-12 rounded-xl object-cover" />}
                            <span className="min-w-0">
                                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Now showing</span>
                                <span className="block truncate font-display text-sm font-bold text-white">{product.name}</span>
                                {product.price_label && <span className="block font-mono text-[11px]" style={{ color: theme.primary }}>{product.price_label}</span>}
                            </span>
                            <ArrowRight size={15} className="ml-auto transition-transform group-hover:translate-x-1" style={{ color: theme.primary }} />
                        </Link>
                    )}
                </div>
            </div>
        </motion.section>
    );
}
